import { useTranslation } from 'react-i18next';
import {
  AppWindow,
  FolderTree,
  Network,
  ShieldCheck,
} from 'lucide-react';
import { List, QrCode, Settings } from 'iconoir-react';
import { Card } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import type { AgentIntegrationCardModel } from './provider-utils';

type AgentIntegrationOverviewCardProps = {
  item: AgentIntegrationCardModel;
  isBusy?: boolean;
  onBrowse?: () => void;
  onStartChatting?: () => void;
  onShowList?: () => void;
  listActionType?: 'list' | 'settings';
  botCount?: number;
  countKind?: 'bot' | 'connection';
};

export const AgentIntegrationOverviewCard = ({
  item,
  isBusy = false,
  onBrowse,
  onStartChatting,
  onShowList,
  listActionType = 'list',
  botCount,
  countKind = 'bot',
}: AgentIntegrationOverviewCardProps) => {
  const { t } = useTranslation();

  const getCountLabel = () => {
    if (countKind === 'connection') {
      return botCount === 1
        ? t('CONNECTION_COUNT_ONE', { defaultValue: '1 connection' })
        : t('CONNECTIONS_COUNT', {
            count: botCount,
            defaultValue: '{{count}} connections',
          });
    }

    return botCount === 1
      ? t('TELEGRAM_BOT_COUNT_ONE', { defaultValue: '1 bot' })
      : t('TELEGRAM_BOTS_COUNT', {
          count: botCount,
          defaultValue: '{{count}} bots',
        });
  };

  const hasTopAction =
    (item.supportsChat && onStartChatting) ||
    (item.supportsList && onShowList) ||
    (item.canBrowse && onBrowse);

  return (
    <Card
      className={cn(
        'flex h-full w-full min-w-0 flex-col gap-3 rounded-lg border border-grey-50 bg-white p-4 shadow-none sm:p-6',
        'dark:border-border dark:bg-card'
      )}
    >
      <div className="flex w-full items-start justify-between gap-3">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[#e7e7e7] bg-white bg-gradient-to-br dark:border-border dark:bg-card',
            item.visual.accentClassName
          )}
        >
          {item.visual.icon ? (
            <img
              alt=""
              className="h-6 w-6 object-contain"
              src={item.visual.icon}
            />
          ) : (
            item.visual.glyph ?? (
              <AppWindow className="h-4 w-4 text-slate-600 dark:text-slate-300" aria-hidden />
            )
          )}
        </div>

        {hasTopAction ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {item.supportsChat && onStartChatting ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-md border-[#e7e7e7] dark:border-border"
                disabled={isBusy}
                onClick={onStartChatting}
                aria-label={t('SCAN_TO_START_CHATTING', { defaultValue: 'Scan to start chatting' })}
              >
                <QrCode width={16} height={16} aria-hidden />
              </Button>
            ) : null}

            {item.supportsList && onShowList ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-md border-[#e7e7e7] dark:border-border"
                disabled={isBusy}
                onClick={onShowList}
                aria-label={
                  listActionType === 'settings'
                    ? t('SETTINGS', { defaultValue: 'Settings' })
                    : t('VIEW_CONNECTIONS', { defaultValue: 'View connections' })
                }
              >
                {listActionType === 'settings' ? (
                  <Settings width={16} height={16} aria-hidden />
                ) : (
                  <List width={16} height={16} aria-hidden />
                )}
              </Button>
            ) : null}

            {item.canBrowse && onBrowse ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-md border-[#e7e7e7] dark:border-border"
                disabled={isBusy}
                onClick={onBrowse}
              >
                <FolderTree className="h-4 w-4" aria-hidden />
                <span className="sr-only">
                  {t('BROWSE_INTEGRATION', {
                    name: item.name,
                    defaultValue: 'Browse {{name}}',
                  })}
                </span>
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <h3 className="w-full truncate text-base font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
          {item.name}
        </h3>

        <p className="line-clamp-3 min-h-[3.75rem] w-full text-sm font-normal leading-[1.6] text-[#4f4f4f] dark:text-muted-foreground">
          {item.description}
        </p>
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-[#e7e7e7] pt-3 text-xs text-[#6d6d6d] dark:border-border dark:text-muted-foreground">
        <span
          className={cn(
            'font-medium',
            item.connected ? 'text-[#079455]' : 'text-[#888888] dark:text-muted-foreground'
          )}
        >
          {item.connected
            ? t('CONNECTED', { defaultValue: 'Connected' })
            : t('DISCONNECTED', { defaultValue: 'Disconnected' })}
        </span>
        {typeof botCount === 'number' ? (
          <>
            <span className="text-[#e7e7e7] dark:text-border">|</span>
            <span>{getCountLabel()}</span>
          </>
        ) : (
          <>
            <span className="text-[#e7e7e7] dark:text-border">|</span>
            <span>{item.providerFamily || t('INTEGRATION', { defaultValue: 'Integration' })}</span>
            <span className="text-[#e7e7e7] dark:text-border">|</span>
            <span className="inline-flex items-center gap-1">
              {item.resourceBrowseSupported ? (
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <Network className="h-3.5 w-3.5" aria-hidden />
              )}
              {item.resourceBrowseSupported
                ? t('BROWSABLE', { defaultValue: 'Browsable' })
                : t('CONNECTION_ONLY', { defaultValue: 'Connection only' })}
            </span>
          </>
        )}
      </div>
    </Card>
  );
};

export const AgentIntegrationOverviewCardSkeleton = () => (
  <Card className="flex h-full min-w-0 flex-col overflow-hidden rounded-lg border border-grey-50 bg-white shadow-none dark:border-border dark:bg-card">
    <div className="flex h-full flex-col gap-3 p-4 sm:p-6">
      <div className="flex w-full items-center justify-between">
        <div className="h-10 w-10 rounded-lg bg-muted" />
        <div className="h-8 w-24 rounded-md bg-muted" />
      </div>
      <div className="h-4 w-32 rounded bg-muted" />
      <div className="h-3 w-full rounded bg-muted" />
      <div className="h-3 w-4/5 rounded bg-muted" />
      <div className="mt-auto border-t border-[#e7e7e7] pt-3 dark:border-border">
        <div className="h-3 w-40 rounded bg-muted" />
      </div>
    </div>
  </Card>
);
